import { Form, Field, FormElement, FormRenderProps } from '@progress/kendo-react-form';
import { Checkbox, Input } from '@progress/kendo-react-inputs';
import { useState, useContext, useEffect } from 'react';

import { SetValueOnObject } from '../../utils/UnsafeTypescriptUtil';
import { Pipet, blankPipet, apiModelToPipet } from '../../models/pipet';
import { useParams } from 'react-router-dom';
import { protectedResources } from '../../authConfig';
import { useFetchWithMsal } from '../../hooks/useFetchWithMsal';
import { LoadPage } from '../../components/LoadPage';
import { StreckDropDownTypes, TypedStreckDropDown } from '../../components/TypedStreckDropDown';
import { PipetContext, AppUserContext } from '../../components/PvsContextProvider';
import { useNotificationAPI } from '../../components/NotificationProvider';

interface ManagePipetProps {
    labId?: number,
    isPopup?: boolean,
    key?: number,
    children?: React.ReactNode
}

const pageTitle = 'Pipet Manager';

const ManagePipet = (props: ManagePipetProps) => {
    const params = useParams();
    const context = useContext(PipetContext);
    const appUserContext = useContext(AppUserContext);
    const { addNotification } = useNotificationAPI();

    const { isLoading, execute } = useFetchWithMsal({
        scopes: protectedResources.scopes.admin
    });

    const [pipet, setPipet] = useState(
        SetValueOnObject(structuredClone(blankPipet), "Id",
            (params.PipetId === undefined ?
                0 :
                params.PipetId)) as Pipet);

    const onChange_Pipet = (e: any, propName: string) => {
        let updatedPipet: Pipet = SetValueOnObject(pipet, propName, e.target.value) as Pipet;
        setPipet(updatedPipet);
    }

    const handleLabChange = (labId: number) => {
        let updatedPipet: Pipet = SetValueOnObject(pipet, "LabId", labId) as Pipet;
        setPipet(updatedPipet);
    }

    const [inactive, setInactive] = useState(false);
    const handleSubmit = (formRenderProps: FormRenderProps) => {
        if (!formRenderProps.valid || pipet.LabId === 0) {
            return;
        }

        const apiEndpoint = pipet.Id > 0 ?
            protectedResources.apiPipet.endpoint + "UpdatePipet" :
            protectedResources.apiPipet.endpoint + "CreatePipet";

        if (!pipet.Volume) { pipet.Volume = undefined; }

        let date = undefined;
        if (inactive) {
            if (pipet.InactiveDate) {
                date = pipet.InactiveDate
            }
            else {
                date = new Date()
            }
        }

        execute(pipet.Id > 0 ? 'PUT' : 'POST', apiEndpoint, {
            Id: pipet.Id,
            LabId: pipet.LabId,
            SerialNumber: pipet.SerialNumber,
            Manufacturer: pipet.Manufacturer,
            Volume: pipet.Volume,
            InactiveDate: date,
        } as Pipet)
            .then(
                ((result: any) => {
                    if (result?.id) {
                        addNotification('Pipet ' + (pipet.Id > 0 ? 'updated' : 'created') + ': ' + pipet.SerialNumber, pageTitle, 'success');
                        context.refresh();
                        if (pipet.Id === 0 && !props.isPopup) {
                            //window.history.replaceState(null, '', '/pipet-manage/' + result?.id);
                            setPipet(SetValueOnObject(structuredClone(blankPipet), "LabId", pipet.LabId) as Pipet);
                        } else {
                            setPipet(apiModelToPipet(result));
                        }
                    }
                }),
            );
    };

    const getPipet = () => {
        if (pipet.Id > 0) {
            return execute('GET', protectedResources.apiPipet.endpoint + "GetPipet/?Id=" + pipet.Id)
                .then(
                    ((result: any) => {
                        if (result) {
                            let model = apiModelToPipet(result);
                            setPipet(model);
                            setInactive(model.InactiveDate !== null && model.InactiveDate !== undefined);
                        }
                    })
                );
        } else {
            setPipet(SetValueOnObject(structuredClone(blankPipet), "LabId",
                (props.labId === undefined ?
                    0 :
                    props.labId)) as Pipet);
        }
        return null;
    }

    useEffect(() => {
        getPipet();
    }, [execute]);

    return <LoadPage isLoading={isLoading}>
        <Form initialValues={pipet} key={JSON.stringify(pipet)} render={(_formRenderProps) =>
            <FormElement style={{ maxWidth: 650 }}>
                <fieldset className={'k-form-fieldset'}>
                    <legend className={'k-form-legend'}>Please fill in the fields:</legend>

                    <div className="row">
                        <div className="mb-3 col">
                            <Field name={'SerialNumber'} component={Input} label={'Serial Number / Pipet Name*'} required={true} onChange={(event) => onChange_Pipet(event, "SerialNumber")} />
                        </div>
                        {pipet.Id > 0 &&
                            <Checkbox label={' :Inactive'} labelPlacement={"before"} checked={inactive} onChange={(event) => setInactive(event.value)} />}
                    </div>

                    <div className="row">
                        <div className="mb-3 col">
                            <TypedStreckDropDown
                                streckDropDownType={StreckDropDownTypes.Lab}
                                handleChange={(result: any) => handleLabChange(result)}
                                selectedId={pipet.LabId}
                                required={true}
                                disabled={pipet.Id > 0}
                            ></TypedStreckDropDown>
                        </div>
                    </div>

                    <div className="row">
                        <div className="mb-3 col">
                            <Field name={'Manufacturer'} component={Input} label={'Manufacturer'} required={false} onChange={(event) => onChange_Pipet(event, "Manufacturer")} />
                        </div>
                    </div>

                    <div className="row">
                        <div className="mb-3 col">
                            <Field name={'Volume'} component={Input} label={'Volume'} required={false} onChange={(event) => onChange_Pipet(event, "Volume")} />
                        </div>
                    </div>
                </fieldset>
                <div className="k-form-buttons">
                    <button onClick={() => handleSubmit(_formRenderProps)} type={'submit'} hidden={appUserContext.isReadOnly} className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base">
                        {pipet.Id > 0 ? "Update" : "Create"}
                    </button>
                </div>
            </FormElement>} />
    </LoadPage>;
};

export default ManagePipet;